import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowRight, Clock, ChevronDown, ShieldCheck } from "lucide-react";
import SeoHead from "../components/SeoHead";
import ReadingProgressBar from "../components/ReadingProgressBar";
import { NewsletterSignup } from "../components/NewsletterSignup";
import { fetchBlogPost, blogImageUrl } from "../lib/blogApi";

const slugifyName = (s) =>
  (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

function ArticleJsonLd({ post }) {
  useEffect(() => {
    if (!post) return undefined;
    const id = "blog-article-jsonld";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    const origin = window.location.origin;
    const blocks = [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.meta_description || post.excerpt,
        url: `${origin}/blog/${post.slug}`,
        datePublished: post.published_at || post.created_at,
        dateModified: post.updated_at || post.created_at,
        author: { "@type": "Organization", name: post.author || "BlendSafe" },
        reviewedBy: {
          "@type": "Organization",
          name: post.reviewed_by || "BlendSafe Medical Review Team",
        },
        publisher: { "@type": "Organization", name: "BlendSafe" },
        ...(post.cover_image_url
          ? { image: blogImageUrl(post.cover_image_url) }
          : {}),
      },
    ];
    if (post.faqs?.length) {
      blocks.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      });
    }
    blocks.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${origin}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: `${origin}/blog/${post.slug}` },
      ],
    });
    el.textContent = JSON.stringify(blocks);
    return () => {
      const t = document.getElementById(id);
      if (t && t.parentNode) t.parentNode.removeChild(t);
    };
  }, [post]);
  return null;
}

function Faq({ item, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-surface-line rounded-xl bg-white overflow-hidden" data-testid={`blog-faq-${idx}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="text-[15px] font-semibold text-ink">{item.question}</span>
        <ChevronDown className={`w-4 h-4 text-ink-mute shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 text-[14px] text-ink-soft/90 leading-relaxed">
          {item.answer}
        </div>
      )}
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus("loading");
    fetchBlogPost(slug)
      .then((d) => { setPost(d); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "notfound") {
    return (
      <main className="bg-cream-50 min-h-[70vh] flex flex-col items-center justify-center px-6 text-center" data-testid="blog-post-notfound">
        <p className="text-lg font-semibold text-ink">Article not found</p>
        <Link to="/blog" className="mt-4 inline-flex items-center gap-2 text-[#1f3d2e] font-semibold">
          <ArrowLeft className="w-4 h-4" /> Back to the blog
        </Link>
      </main>
    );
  }

  if (status === "loading" || !post) {
    return (
      <main className="bg-cream-50 min-h-[70vh] flex items-center justify-center" data-testid="blog-post-loading">
        <p className="text-ink-mute">Loading…</p>
      </main>
    );
  }

  const cover = blogImageUrl(post.cover_image_url);

  return (
    <main className="bg-cream-50" data-testid="blog-post-page">
      <ReadingProgressBar />
      <SeoHead
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        canonicalPath={`/blog/${post.slug}`}
      />
      <ArticleJsonLd post={post} />

      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <button
          onClick={() => navigate("/blog")}
          data-testid="blog-post-back"
          className="inline-flex items-center gap-2 text-[13px] font-semibold text-ink-mute hover:text-ink transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> All articles
        </button>

        <div className="mt-6 flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1f3d2e] text-cream-50 text-[11px] font-semibold">
            {post.category_label}
          </span>
          <span className="inline-flex items-center gap-1.5 text-[12px] text-ink-mute font-medium">
            <Clock className="w-3.5 h-3.5" /> {post.reading_minutes} min read
          </span>
        </div>

        <h1
          data-testid="blog-post-title"
          className="mt-4 text-3xl sm:text-4xl lg:text-[46px] font-extrabold tracking-[-0.03em] text-ink leading-[1.06]"
        >
          {post.title}
        </h1>

        {/* Byline — E-E-A-T trust line */}
        <div
          data-testid="blog-post-byline"
          className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 border-y border-surface-line py-3.5"
        >
          <span className="text-[13px] text-ink-soft">
            Written by <span className="font-semibold text-ink">{post.author || "BlendSafe"}</span>
          </span>
          <span className="text-ink-mute/50">·</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#eef3ec] text-[12px] font-semibold text-[#1f3d2e]">
            <ShieldCheck className="w-3.5 h-3.5" />
            Reviewed by {post.reviewed_by || "BlendSafe Medical Review Team"}
          </span>
          {(post.updated_at || post.published_at) && (
            <>
              <span className="text-ink-mute/50 hidden sm:inline">·</span>
              <span className="text-[12.5px] text-ink-mute">
                Updated{" "}
                {new Date(post.updated_at || post.published_at).toLocaleDateString("en-US", {
                  month: "short", day: "numeric", year: "numeric",
                })}
              </span>
            </>
          )}
        </div>

        {cover && (
          <img
            src={cover}
            alt={post.title}
            className="mt-7 w-full rounded-[20px] border border-surface-line shadow-card"
            data-testid="blog-post-cover"
          />
        )}

        <div
          className="blog-prose mt-8"
          data-testid="blog-post-body"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {post.body_markdown}
          </ReactMarkdown>
        </div>

        {/* Pillar hub link — routes readers into the SEO hub page */}
        {post.anchor_type && post.anchor_name && (
          <Link
            to={
              post.anchor_type === "medication"
                ? `/medications/${slugifyName(post.anchor_name)}`
                : `/ingredients/${slugifyName(post.anchor_name)}`
            }
            data-testid="blog-post-pillar-link"
            className="mt-8 flex items-center justify-between gap-4 rounded-2xl border border-[#1f3d2e]/20 bg-[#eef3ec] p-5 hover:bg-[#e5ede2] transition-colors"
          >
            <div>
              <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#1f3d2e]">
                Full reference
              </span>
              <p className="mt-1 text-[15px] font-semibold text-ink">
                {post.anchor_type === "medication"
                  ? `See the full ${post.anchor_name} compatibility list`
                  : `See the full ${post.anchor_name} ingredient guide`}
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-[#1f3d2e] shrink-0" />
          </Link>
        )}

        {post.faqs?.length > 0 && (
          <section className="mt-12" data-testid="blog-post-faqs">
            <h2 className="text-2xl font-extrabold tracking-[-0.02em] text-ink">
              Frequently asked questions
            </h2>
            <div className="mt-5 space-y-3">
              {post.faqs.map((f, i) => (
                <Faq key={i} item={f} idx={i} />
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="mt-12 rounded-[22px] bg-[#1f3d2e] text-cream-50 p-7 sm:p-9 text-center">
          <p className="text-xl sm:text-2xl font-extrabold tracking-[-0.02em]">
            Not sure if your routine is safe?
          </p>
          <p className="mt-2 text-[14px] text-cream-50/80 max-w-md mx-auto">
            Get an instant, evidence-based verdict for your exact medication and
            skincare — free, no signup.
          </p>
          <Link
            to="/check"
            data-testid="blog-post-cta"
            className="mt-6 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-cream-50 text-ink text-[14px] font-bold hover:scale-[1.02] transition-transform"
          >
            Check my skincare <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Newsletter */}
        <div className="mt-12">
          <NewsletterSignup source="blog-post" variant="light" />
        </div>

        {/* Related */}
        {post.related?.length > 0 && (
          <section className="mt-14" data-testid="blog-post-related">
            <h2 className="text-lg font-extrabold text-ink">Keep reading</h2>
            <div className="mt-5 grid sm:grid-cols-3 gap-5">
              {post.related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  data-testid={`blog-related-${r.slug}`}
                  className="block rounded-2xl border border-surface-line bg-white p-4 hover:shadow-card transition-all hover:-translate-y-0.5"
                >
                  <span className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-ink-mute">
                    {r.category_label}
                  </span>
                  <p className="mt-1.5 text-[14.5px] font-semibold text-ink leading-snug line-clamp-3">
                    {r.title}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </main>
  );
}
